import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslationList } from '../store/selectors/translationsSelectors';
import { useAppDispatch } from './StoreHooks';
import { fetchTranslations, patchTranslationsThunk } from '../store/thunk/translationsThunk';

interface UseTranslationsParams {
    open: boolean;
    entityType: string;
    entityId: string;
    property: string;
    nestedId?: string[];
    onClose: () => void;
}

export const useTranslations = ({
    open,
    entityType,
    entityId,
    property,
    nestedId,
    onClose
}: UseTranslationsParams) => {
    const dispatch = useAppDispatch();
    const translationList = useTranslationList(entityType, entityId, property);
    const [translations, setTranslations] = useState<Record<string, string>>({});
    const [flagError, setFlagError] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (open) {
            dispatch(fetchTranslations({ entityType, entityId, property }));
        }
    }, [open, entityType, entityId, property, dispatch]);

    useEffect(() => {
        if (translationList) {
            setTranslations(translationList);
        }
    }, [translationList]);

    const originalTranslations = useMemo(() => translationList || {}, [translationList]);
    const hasChanges = useMemo(() => {
        return JSON.stringify(originalTranslations) !== JSON.stringify(translations);
    }, [originalTranslations, translations]);
    const setTranslation = (lang: string, value: string) => {
        setTranslations((prev) => ({ ...prev, [lang]: value }));
    };
    const handleFlagError = (lang: string) => {
        setFlagError((prev) => ({ ...prev, [lang]: true }));
    };
    const handleSave = useCallback(async () => {
        await dispatch(
            patchTranslationsThunk({
                entityType,
                entityId,
                property,
                translations,
                nestedId,
            })
        );
        onClose();
    }, [dispatch, entityType, entityId, property, translations, nestedId, onClose]);

    return {
        translations,
        hasChanges,
        setTranslation,
        handleSave,
        flagError,
        handleFlagError,
    };
};
